import React from "react";
import {
  Table,
  Space,
  Button,
  Card,
  Row,
  Col,
  Input,
  message,
  Spin,
  Modal,
  Image,
  Switch,
  Tag,
  Tooltip,
  Select,
  App,
} from "antd";
import { useEffect, useState } from "react";
import {
  SearchOutlined,
  FileOutlined,
  FileImageOutlined,
  FilePdfOutlined,
  FileWordOutlined,
  FileExcelOutlined,
  FilePptOutlined,
} from "@ant-design/icons";
import { RiAddBoxFill } from "react-icons/ri";
import dayjs, { Dayjs } from "dayjs";
import { AuthContext } from "../../Auth/AuthContext";
import { FaEdit, FaPen, FaTrashAlt } from "react-icons/fa";
import { FaSyncAlt } from "react-icons/fa";
import type { SorterResult, SortOrder } from "antd/es/table/interface";
import type { ColumnsType } from "antd/es/table";
import { hasPermission } from "../../utils/HasPermission";
import {
  getAllCampOffAttendance,
  deleteCampoffData,
  getallDeveloperForTask,
} from "../../utils/API";
import AddCampassoffModal from "./Add";
import { Campusaprovemodal } from "./Aprove";
import { autoRegisterCurrentPageColumns } from "../../utils/emailTemplateColumns";

// Define the data type for the table
// Update your interface
interface CampassoffPage {
  _id: string;
  key: string;
  userId: string | { _id: string; username: string };
  username?: string;
  date: string;
  day: string;
  clockIn: string;
  lunchStart: string;
  lunchEnd?: string;
  endTime?: string;
  requestStatus?: string;
  lunchComplete: string;
  clockOut: string;
  totalTime: string;
  highlightRed?: boolean;
  status: "Approved";
  attendanceId: string; // <-- Add this line
  remark: string;
}
interface AuthData {
  roleId: {
    roleId: number;
  };
}

const CampOffPage: React.FC = () => {
  const [page, setPage] = useState(1);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loadingDevelopers, setLoadingDevelopers] = useState(false);
  const [isApproveModalOpen, setIsApproveModalOpen] = useState(false);
  const [developers, setDevelopers] = useState<CampassoffPage[]>([]);
  const [selectedMonth, setSelectedMonth] = useState(dayjs().format("YYYY-MM"));
  const [currentRecord, setCurrentRecord] = useState<CampassoffPage | null>(
    null,
  );
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [filteredDevelopers, setFilteredDevelopers] = useState<
    CampassoffPage[]
  >([]);
  const [currentTask, setCurrentTask] = useState<CampassoffPage | null>(null);
  const [customers, setCustomers] = useState<CampassoffPage[]>([]);
  const [selectedDeveloper, setSelectedDeveloper] = useState<string | null>(
    null,
  );
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState<CampassoffPage | null>(
    null,
  );
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [totalRecords, setTotalRecords] = useState(0);
  const [filteredData, setFilteredData] = useState<CampassoffPage[]>([]);
  const [searchText, setSearchText] = useState("");
  const [isAddTaskModalOpen, setIsAddTaskModalOpen] = useState(false);
  const authData: any = localStorage.getItem("authData") || {};
  const parsedAuthData = authData ? JSON.parse(authData) : "";
  const isAdmin = parsedAuthData?.roleId?.roleId === 1;

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  type ParamsType = {
    _?: any;
    page: any;
  };
  interface Developer {
    _id: string;
    username: string;
    // Add other developer properties if needed
  }
  type StatusType =
    | "Present"
    | "Absent"
    | "HalfDay"
    | "Leave"
    | "Sunday"
    | "Holiday"
    | "WeekOff"
    | "HalfLeave"
    | "Approved";
  const statusStyles = {
    Present: {
      background: "#dcfce7",
      text: "#15803d",
    },
    Absent: {
      background: "#fee2e2",
      text: "#b91c1c",
    },
    HalfDay: {
      background: "#dbeafe",
      text: "#1d4ed8",
    },
    Leave: {
      background: "#fef9c3",
      text: "#ca8a04",
    },
    Sunday: {
      background: "#ede9fe",
      text: "#7c3aed",
    },
    Holiday: {
      background: "#fce7f3",
      text: "#db2777",
    },
    WeekOff: {
      background: "#ffedd5",
      text: "#ea580c",
    },
    HalfLeave: {
      background: "#dcfce7",
      text: "#15803d",
    },
    Approved: {
      background: "#DCFCE7",
      text: "#166534",
    },
  };

  useEffect(() => {
    fetchCampOffAttendance();
    if (authData) {
      fetchDevelopers();
    }
  }, [authData, selectedDeveloper]);

  const fetchDevelopers = async () => {
    try {
      setLoadingDevelopers(true);
      const result = await getallDeveloperForTask("", "");
      if (result?.data) {
        setDevelopers(result.data);
      } else {
        message.warning("No developers found");
        setDevelopers([]);
      }
    } catch (error) {
      console.error("Error fetching developers:", error);
      message.error("Failed to fetch developers");
      setDevelopers([]);
    } finally {
      setLoadingDevelopers(false);
    }
  };
  const fetchCampOffAttendance = async () => {
    setLoading(true);
    try {
      // Get the auth data properly
      const authData = localStorage.getItem("authData");
      const parsedAuthData = authData ? JSON.parse(authData) : {};

      // Determine if user is admin
      const isAdmin = parsedAuthData?.roleId?.roleId === 1;

      // Set userId based on role
      const userId = isAdmin
        ? selectedDeveloper || null // Admin can see all or filtered by selected developer
        : parsedAuthData?.user?._id; // Non-admin sees only their own data

      const response = await getAllCampOffAttendance(userId, page);

      if (!response?.success) {
        throw new Error(response?.message || "Failed to fetch data");
      }

      const dataWithKeys = response.data.map((item: any) => ({
        ...item,
        key: item._id,
        day: dayjs(item.date).format("dddd"),
        totalTime: item.totalTime || formatRemainingTime(item),
        highlightRed: item.status === "Approved",
        username: item.userId?.username || "-",
        attendanceId: item.attendanceId || item._id,
      }));

      setCustomers(dataWithKeys);
      setFilteredData(dataWithKeys);
      setPagination((prev) => ({
        ...prev,
        total: response.totalRecords || 0,
        current: response.currentPage || 1,
      }));
    } catch (error) {
      console.error("Fetch error:", error);
      message.error("Failed to fetch attendance data.");
    } finally {
      setLoading(false);
    }
  };
  // Update the useEffect dependency
  useEffect(() => {
    fetchCampOffAttendance();
  }, [pagination.current]); // Trigger when current page changes

  // ... rest of your existing functions (formatRemainingTime, etc.)
  const formatRemainingTime = (record: any) => {
    if (!record?.clockIn) return "Remaining: 8h 0m";

    const clockInTime = dayjs(record.clockIn, "HH:mm");
    const clockOutTime = record.clockOut
      ? dayjs(record.clockOut, "HH:mm")
      : dayjs();

    let totalMinutes = clockOutTime.diff(clockInTime, "minute");

    // Subtract lunch duration if available
    if (record.lunchStart && record.lunchEnd) {
      const lunchStart = dayjs(record.lunchStart, "HH:mm");
      const lunchEnd = dayjs(record.lunchEnd, "HH:mm");
      const lunchDuration = lunchEnd.diff(lunchStart, "minute");
      totalMinutes -= lunchDuration;
    }

    // For completed days, show total time
    if (record.clockOut) {
      const hours = Math.floor(totalMinutes / 60);
      const minutes = totalMinutes % 60;
      return `${hours}h ${minutes}m`;
    }

    // For current day, show remaining time
    const remaining = Math.max(480 - totalMinutes, 0); // 8 hours = 480 minutes
    const hours = Math.floor(remaining / 60);
    const minutes = remaining % 60;
    return `Remaining: ${hours}h ${minutes}m`;
  };
  useEffect(() => {
    fetchCampOffAttendance();
  }, [page]);

  // Handle search
  useEffect(() => {
    if (searchText) {
      const filtered = customers.filter((item) =>
        Object.values(item).some((val) => {
          if (val && typeof val === "object") {
            return Object.values(val).some((nestedVal) =>
              String(nestedVal)
                .toLowerCase()
                .includes(searchText.toLowerCase()),
            );
          }
          return String(val).toLowerCase().includes(searchText.toLowerCase());
        }),
      );
      setFilteredData(filtered);
    } else {
      setFilteredData(customers);
    }
  }, [searchText, customers]);

  const handleDelete = async (deleteId: string) => {
    if (deleteId) {
      try {
        const response: any = await deleteCampoffData(deleteId);
        if (response) {
          message.success("Record deleted successfully");
          fetchCampOffAttendance();
        } else {
          message.error("Failed to delete record");
        }
      } catch (error) {
        message.error("Error deleting record. Please try again.");
      } finally {
        setDeleteId(null);
      }
    }
  };

  const handleDeleteConfirmation = (id: string) => {
    setDeleteId(id);
  };

  // Define columns
  const columns: ColumnsType<CampassoffPage> = [
    {
      title: "#",
      key: "index",
      fixed: "left" as const,
      render: (_text, _record, index) => index + 1,
    },

    ...(isAdmin
      ? [
          {
            title: "Developer Name",
            dataIndex: "userId",
            key: "username",
            fixed: "left" as const,
            render: (userId: string | { _id: string; username: string }) => {
              if (typeof userId === "object") {
                return userId.username || "-";
              }
              const user = customers.find((c) => c.userId === userId)?.username;
              return user || "-";
            },
          },
        ]
      : []),
    {
      title: "Date",
      dataIndex: "date",
      key: "date",
      fixed: "left" as const,
      render: (date: string) => {
        if (!date) return "-";
        const parsedDate = dayjs(date, "DD/MM/YYYY");
        return parsedDate.isValid() ? parsedDate.format("DD-MM-YYYY") : date;
      },
      sorter: (a, b) => {
        const dateA = dayjs(a.date, "DD/MM/YYYY");
        const dateB = dayjs(b.date, "DD/MM/YYYY");
        return dateA.isValid() && dateB.isValid()
          ? dateA.unix() - dateB.unix()
          : 0;
      },

      sortDirections: ["descend", "ascend"] as SortOrder[],
      defaultSortOrder: "descend" as SortOrder,
    },
    {
      title: "Day",
      dataIndex: "date",
      key: "day",
      fixed: "left" as const,
      render: (date) => {
        if (!date) return "-";
        const parsedDate = dayjs(date, "DD/MM/YYYY");
        return parsedDate.isValid() ? parsedDate.format("dddd") : "-";
      },
    },
    { title: "Clock In", dataIndex: "clockIn", key: "clockIn" },
    { title: "Lunch Start", dataIndex: "lunchStart", key: "lunchStart" },
    { title: "Lunch Complete", dataIndex: "lunchEnd", key: "lunchEnd" },
    { title: "Clock Out", dataIndex: "clockOut", key: "clockOut" },
    {
      title: "Total Time",
      dataIndex: "endTime",
      key: "totalTime",
      render: (_: any, record) => {
        // Return the totalTime value or a fallback
        return record.totalTime || "-";
      },
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status: StatusType) => {
        const style = statusStyles[status] || {
          background: "#DCFCE7",
          text: "#366534",
        };
        return (
          <span
            className="badge rounded-pill"
            style={{
              background: style.background,
              color: style.text,
              fontSize: "10px",
              padding: "3px 6px",
              borderRadius: "8px",
            }}
          >
            {status || "Unknown"}
          </span>
        );
      },
    },
    {
      title: "Remark",
      dataIndex: "remark",
      key: "remark",
      render: (text: string) => {
        if (!text) return "-";

        const truncated = text.length > 20 ? text.slice(0, 20) + "..." : text;

        return (
          <Tooltip
            title={text}
            placement="topLeft"
            color="#CCCCCC"
            overlayInnerStyle={{
              color: "#000", // ✅ font color
              padding: "12px",
            }}
          >
            <span
              style={{
                display: "inline-block",
                maxWidth: 200,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {truncated}
            </span>
          </Tooltip>
        );
      },
    },
    {
      title: "Action",
      dataIndex: "action",
      render: (_: any, record: CampassoffPage, index: number) => {
        return (
          <div className="d-flex" style={{ gap: "8px", alignItems: "center" }}>
            {/* Delete button - only for developers */}
            {authData &&
              parsedAuthData?.roleId?.roleId === 1 &&
              hasPermission("attendances", "delete") && (
                <Tooltip title="Delete">
                  <button
                    onClick={() => handleDeleteConfirmation(record._id)}
                    className={`w-7 h-7 rounded-full 
                                        bg-gradient-to-br from-rose-500 to-rose-700 
                                        shadow-[0_4px_12px_rgba(244,63,94,0.3)] 
                                        hover:shadow-[0_6px_16px_rgba(244,63,94,0.4)]
                                        active:translate-y-1 active:shadow-none 
                                        text-white flex items-center justify-center 
                                        transition-all duration-200 group`}
                    style={{ minWidth: "28px", minHeight: "28px" }}
                  >
                    <FaTrashAlt
                      className="group-hover:scale-110 transition-transform"
                      size={12}
                    />
                  </button>
                </Tooltip>
              )}

            {/* View Updated Data button - only for admins */}
            {authData && parsedAuthData?.roleId?.roleId === 1 && (
              <Tooltip title="View Updated Data">
                <span
                  onClick={() => {
                    setCurrentRecord(record);
                    console.log("Current Record:", record);
                    setIsApproveModalOpen(true);
                  }}
                  className="w-6 h-6 rounded-full bg-gradient-to-br from-cyan-400 to-emerald-500 
                                    shadow-[0_4px_12px_rgba(34,211,238,0.3)] 
                                    hover:shadow-[0_6px_16px_rgba(34,211,238,0.4)] 
                                    active:translate-y-1 active:shadow-none 
                                    text-white flex items-center justify-center 
                                    transition-all duration-200 group cursor-pointer"
                >
                  <FaSyncAlt
                    className="group-hover:scale-110 transition-transform"
                    size={12}
                  />
                </span>
              </Tooltip>
            )}
          </div>
        );
      },
    },
  ];

  useEffect(() => {
    autoRegisterCurrentPageColumns(columns);
  }, []);

  return (
    <div>
      {/* Header & Search */}
      <Row
        className="m-2"
        align="middle"
        justify="space-between"
        gutter={[16, 16]}
      >
        {/* Left: Heading + Developer Select */}
        <Col xs={24} sm={24} md={6} lg={6} xl={10}>
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2">
            <h2 className="headername w-100">Campus Off</h2>
            {parsedAuthData?.roleId?.roleId === 1 && (
              <Select
                showSearch
                style={{ width: "100%", marginLeft: "10vw", height: "33px" }}
                placeholder="Select developer"
                value={selectedDeveloper}
                onChange={(value: string) => {
                  setSelectedDeveloper(value || null);
                  setPage(1);
                }}
                allowClear
                onClear={() => {
                  setSelectedDeveloper(null);
                  setPage(1);
                }}
                optionFilterProp="children"
                filterOption={(input, option) =>
                  (option?.children?.toString()?.toLowerCase() || "").includes(
                    input.toLowerCase(),
                  )
                }
              >
                {developers.map((dev) => (
                  <Select.Option key={dev._id} value={dev._id}>
                    {dev.username}
                  </Select.Option>
                ))}
              </Select>
            )}
          </div>
        </Col>

        {/* Right: Search + Add Button */}
        <Col xs={24} md={12}>
          <div className="flex flex-col sm:flex-row gap-2 justify-end">
            <Input
              prefix={<SearchOutlined />}
              size="large"
              placeholder="Search..."
              allowClear
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              style={{ height: "33px", width: 200 }}
            />

            <Button
              type="primary"
              onClick={() => setIsAddTaskModalOpen(true)}
              className="allbuttons "
            >
              <RiAddBoxFill className="button-icon" />
              <span className="button-text">Add</span>
            </Button>

            <AddCampassoffModal
              visible={isAddTaskModalOpen}
              onClose={() => setIsAddTaskModalOpen(false)}
              onSuccess={() => {
                setIsAddTaskModalOpen(false);
                fetchCampOffAttendance(); // This will refresh the data
              }}
            />
          </div>
        </Col>
      </Row>

      <Card style={{ borderRadius: 10 }}>
        <div
          className="scrollbar-hide custom-scroll-table"
          style={{ overflowX: "auto" }}
        >
          <Table
            rowClassName={(record) =>
              record.status === "Approved" ? "bg-CampOff-row" : ""
            }
            columns={columns}
            dataSource={filteredData}
            scroll={{ x: "max-content" }}
            rowKey={(record) => record._id || record.key}
            loading={loading}
            pagination={{
              ...pagination,
              showSizeChanger: false,
              onChange: (page) => {
                setPagination((prev) => ({ ...prev, current: page }));
              },
            }}
          />
        </div>
      </Card>

      {/* Delete Confirmation Modal */}
      <Modal
        open={!!deleteId} // <-- This makes the modal open when deleteId has a value
        onOk={() => {
          if (deleteId) {
            handleDelete(deleteId);
            setDeleteId(null);
          }
        }}
        onCancel={() => setDeleteId(null)}
        okText="Yes"
        cancelText="No"
        // footer={null}
        width={500}
        centered={false}
        destroyOnClose
        className="custom-modal"
        style={{
          bottom: 20,
          top: "10vw",
          maxHeight: "calc(100vh - 30px)",
          margin: "0 auto",
          position: "relative",
        }}
        title={
          <div
            style={{
              marginLeft: "11px",
              width: "100%",
              paddingRight: 20,
            }}
          >
            Confirmation
          </div>
        }
        okButtonProps={{
          style: {
            backgroundColor: "#ff4d4f",
            borderColor: "#ff4d4f",
            color: "white",
          },
        }}
        bodyStyle={{
          padding: "22px",
          overflowY: "auto",
          fontFamily: "inter, sans-serif",
        }}
        styles={{
          header: {
            borderBottom: "1px solid #e0e0e0",
            padding: "12px",
            position: "sticky",
            top: 0,
            backgroundColor: "white",
          },
          content: {
            padding: 0,
            overflow: "hidden",
          },
          footer: {
            borderTop: "1px solid #e0e0e0",
            padding: "12px",
            position: "sticky",
            top: 0,
            backgroundColor: "white",
          },
        }}
      >
        <p>Are you sure you want to delete this Campus off?</p>
      </Modal>

      {isApproveModalOpen && (
        // In your parent component where you render EditCustomersModal:

        <Campusaprovemodal
          key={currentRecord?._id || "default-key"}
          visible={isApproveModalOpen}
          onClose={() => setIsApproveModalOpen(false)}
          record={currentRecord}
          onSuccess={() => {
            setIsApproveModalOpen(false);
            fetchCampOffAttendance();
          }}
        />
      )}
    </div>
  );
};

export const CAMPOFF_TABLE_COLUMNS = [
  "userId",
  "date",
  "clockIn",
  "lunchStart",
  "lunchEnd",
  "clockOut",
  "totalTime",
  "status",
  "remark",
];

export default CampOffPage;
